import { useEffect, useState } from "react";
import { Bell, Loader2, Plus, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import {
  fetchReminderPrefs,
  saveReminderPrefs,
  requestPermission,
  checkPermission,
  applySchedule,
  cancelAll,
  getCapabilities,
} from "../lib/practiceReminders";
import logger from "../lib/logger";

// ISO day labels (Monday-first, matching the API's 0..6 convention).
const DAYS = [
  { iso: 0, label: "M" },
  { iso: 1, label: "T" },
  { iso: 2, label: "W" },
  { iso: 3, label: "T" },
  { iso: 4, label: "F" },
  { iso: 5, label: "S" },
  { iso: 6, label: "S" },
];

const DEFAULT_TIMES = ["09:00", "20:00"];

/**
 * Reminders settings card.
 *
 * Behaviour:
 *   • Loads saved schedule from backend on mount.
 *   • Enable toggle → asks for OS/browser permission the first time.
 *   • Time inputs + day pills describe *when* to fire.
 *   • Save → PUTs prefs + re-schedules OS-level notifications.
 */
export const PracticeRemindersCard = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [permission, setPermission] = useState("prompt");
  const [enabled, setEnabled] = useState(false);
  const [times, setTimes] = useState(DEFAULT_TIMES);
  const [days, setDays] = useState([0, 1, 2, 3, 4, 5, 6]);
  const caps = getCapabilities();

  useEffect(() => {
    (async () => {
      try {
        const [prefs, perm] = await Promise.all([
          fetchReminderPrefs(),
          checkPermission(),
        ]);
        setEnabled(Boolean(prefs.enabled));
        setTimes(prefs.times?.length ? prefs.times : DEFAULT_TIMES);
        setDays(prefs.days?.length ? prefs.days : [0, 1, 2, 3, 4, 5, 6]);
        setPermission(perm || "prompt");
      } catch (e) {
        logger.warn("Failed to load reminder prefs", e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (caps.kind === "unsupported") {
    // Very old browsers / non-notification environments — hide the card.
    return null;
  }

  const toggleDay = (iso) => {
    setDays((prev) => {
      const has = prev.includes(iso);
      if (has) return prev.filter((d) => d !== iso);
      return [...prev, iso].sort((a, b) => a - b);
    });
  };

  const updateTime = (idx, value) => {
    setTimes((prev) => {
      const next = [...prev];
      next[idx] = value;
      return next;
    });
  };

  const removeTime = (idx) => {
    setTimes((prev) => prev.filter((_, i) => i !== idx));
  };

  const addTime = () => {
    setTimes((prev) => (prev.length >= 4 ? prev : [...prev, "12:00"]));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Ensure permission if user is turning reminders on.
      if (enabled && permission !== "granted") {
        const ok = await requestPermission();
        if (!ok) {
          setSaving(false);
          const denialCopy =
            caps.kind === "native"
              ? "Notifications are blocked. Enable them in iOS Settings → Bilateral Calm → Notifications."
              : "Notifications are blocked in this browser. Open browser settings to allow them, then try again.";
          toast.error(denialCopy);
          setPermission("denied");
          return;
        }
        setPermission("granted");
      }
      const prefs = {
        enabled,
        times: enabled ? times.filter(Boolean) : [],
        days,
        message: "Two minutes of Bilateral Calm is waiting for you.",
      };
      await saveReminderPrefs(prefs);
      if (enabled) {
        await applySchedule(prefs);
      } else {
        await cancelAll();
      }
      toast.success(enabled ? "Practice reminders saved." : "Reminders turned off.");
    } catch (err) {
      logger.error("Save reminders failed", err);
      const msg = err?.response?.data?.detail || "Could not save reminders. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Could not save reminders.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div
        className="bg-white rounded-2xl p-6 sm:p-8 shadow-[0_4px_20px_rgba(0,0,0,0.03)] border border-slate-100 mb-6"
        data-testid="practice-reminders-loading"
      >
        <div className="flex items-center gap-2 text-[#718096]">
          <Loader2 className="w-4 h-4 animate-spin" />
          Loading reminders...
        </div>
      </div>
    );
  }

  const persistenceCopy = caps.persistent
    ? "Reminders fire even when the app is closed."
    : "Reminders fire while the app is open in this browser. Keep a tab open, or use the iOS app for background alerts.";

  return (
    <div
      className="bg-white rounded-2xl p-6 sm:p-8 shadow-[0_4px_20px_rgba(0,0,0,0.03)] border border-slate-100 mb-6 animate-fade-in-up"
      data-testid="practice-reminders-card"
    >
      <div className="flex items-start justify-between gap-4 mb-3">
        <div className="flex-1 min-w-0">
          <h2 className="font-outfit font-medium text-lg text-[#1B2349] mb-1 flex items-center gap-2">
            <Bell className="w-5 h-5 text-[#3090A8]" />
            Practice reminders
          </h2>
          <p className="text-sm text-[#718096]">
            Gentle nudges to sit for two minutes of Bilateral Calm. {persistenceCopy}
          </p>
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={enabled}
          onClick={() => setEnabled((v) => !v)}
          className={`relative inline-flex items-center h-7 w-12 flex-shrink-0 rounded-full transition-colors duration-200 ${
            enabled ? "bg-[#3090A8]" : "bg-slate-300"
          }`}
          data-testid="reminders-enable-toggle"
        >
          <span
            className={`inline-block w-5 h-5 bg-white rounded-full shadow transform transition-transform duration-200 ${
              enabled ? "translate-x-6" : "translate-x-1"
            }`}
          />
        </button>
      </div>

      {enabled && (
        <>
          <div className="mt-4">
            <div className="text-sm font-medium text-[#1B2349] mb-2">Times</div>
            <div className="space-y-2">
              {times.map((t, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <input
                    type="time"
                    value={t}
                    onChange={(e) => updateTime(idx, e.target.value)}
                    className="rounded-lg border border-slate-200 px-3 py-2 text-[#1B2349] font-medium focus:outline-none focus:ring-2 focus:ring-[#3090A8]/40"
                    data-testid={`reminder-time-${idx}`}
                  />
                  {times.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeTime(idx)}
                      className="p-2 text-[#718096] hover:text-[#D98880] transition-colors rounded-full"
                      aria-label="Remove time"
                      data-testid={`reminder-time-remove-${idx}`}
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
              ))}
              {times.length < 4 && (
                <button
                  type="button"
                  onClick={addTime}
                  className="inline-flex items-center gap-1 text-sm text-[#3090A8] hover:text-[#1B2349] transition-colors"
                  data-testid="reminder-time-add"
                >
                  <Plus className="w-4 h-4" />
                  Add another time
                </button>
              )}
            </div>
          </div>

          <div className="mt-5">
            <div className="text-sm font-medium text-[#1B2349] mb-2">Days</div>
            <div className="flex flex-wrap gap-2">
              {DAYS.map((d) => {
                const active = days.includes(d.iso);
                return (
                  <button
                    key={d.iso}
                    type="button"
                    onClick={() => toggleDay(d.iso)}
                    className={`w-9 h-9 rounded-full border-2 text-sm font-medium transition-colors ${
                      active
                        ? "bg-[#3090A8] border-[#3090A8] text-white"
                        : "bg-white border-slate-200 text-[#718096] hover:border-[#3090A8]"
                    }`}
                    data-testid={`reminder-day-${d.iso}`}
                    aria-pressed={active}
                    aria-label={`Day ${d.iso}`}
                  >
                    {d.label}
                  </button>
                );
              })}
            </div>
          </div>

          {permission === "denied" && (
            <p
              className="mt-4 text-sm text-[#D98880]"
              data-testid="reminders-denied-warning"
            >
              Notifications are currently blocked. {caps.kind === "native"
                ? "Open iOS Settings → Bilateral Calm → Notifications to allow them."
                : "Allow notifications for this site in your browser settings, then try again."}
            </p>
          )}
        </>
      )}

      <div className="mt-6">
        <Button
          onClick={handleSave}
          disabled={saving}
          className="rounded-full bg-[#1B2349] text-white hover:bg-[#0F1838]"
          data-testid="reminders-save-btn"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save reminders"}
        </Button>
      </div>
    </div>
  );
};

export default PracticeRemindersCard;
